import React, { useState } from 'react';
import api from '../lib/api';
import CornerPanel from '../components/CornerPanel';
import { Play } from 'lucide-react';
import { toast } from 'sonner';
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip } from 'recharts';

export default function Backtest() {
  const [form, setForm] = useState({ symbol: 'AAPL', days: 180, risk: 1000 });
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);

  const run = async (e) => {
    e.preventDefault();
    setBusy(true);
    setRes(null);
    try {
      const { data } = await api.get(`/backtest/${form.symbol.toUpperCase()}?days=${form.days}&risk=${form.risk}`);
      setRes(data);
      toast.success(`Backtest complete — ROI ${data.roi_pct}%`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Backtest failed');
    } finally {
      setBusy(false);
    }
  };

  const Stat = ({ label, value, color }) => (
    <div>
      <div className="text-[10px] tracking-[0.25em] uppercase text-cyan-sys">{label}</div>
      <div className={`font-mono text-xl ${color}`}>{value}</div>
    </div>
  );

  const chartData = (res?.equity_curve || []).map((p) => ({
    t: new Date(p.t).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    equity: p.equity,
  }));

  return (
    <div className="p-4 grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-3">
      <CornerPanel title="Backtest Config" testId="backtest-config">
        <form onSubmit={run} className="space-y-3">
          {['symbol','days','risk'].map((k) => (
            <label key={k} className="block">
              <span className="block text-[10px] tracking-[0.3em] uppercase text-cyan-sys mb-1">
                {k === 'risk' ? 'risk per trade ($)' : k === 'days' ? 'lookback (days)' : 'symbol'}
              </span>
              <input
                data-testid={`backtest-field-${k}`}
                value={form[k]}
                onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                className="w-full bg-black border border-[var(--border-muted)] focus:border-cyan-sys outline-none px-2 py-1.5 font-mono text-[13px]"
              />
            </label>
          ))}
          <button
            data-testid="backtest-run"
            disabled={busy}
            className="w-full flex items-center justify-center gap-2 border border-buy text-buy hover:bg-buy hover:text-black font-display tracking-[0.3em] uppercase text-[11px] py-2 disabled:opacity-50"
          >
            <Play size={12} /> {busy ? 'simulating...' : 'run simulation'}
          </button>
        </form>
        <div className="mt-4 p-2 border border-[var(--border-default)] text-[10px] font-mono text-[var(--txt-secondary)]">
          // STRATEGY: long-only, BUY signal + firewall-clear only. Stop/target from ATR.
          Exit: stop, target, or 20-bar timeout.
        </div>
      </CornerPanel>

      <CornerPanel title={`Results // ${res?.symbol || '—'}`} active={!!res} testId="backtest-results">
        {!res && <div className="text-[var(--txt-muted)]">// configure and run a simulation.</div>}
        {res && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <Stat label="Final Equity" value={`$${res.final_equity.toLocaleString()}`} color="text-cyan-sys" />
              <Stat label="ROI" value={`${res.roi_pct}%`} color={res.roi_pct >= 0 ? 'text-buy' : 'text-sell'} />
              <Stat label="Win Rate" value={`${res.win_rate}%`} color="text-hold" />
              <Stat label="Max DD" value={`${res.max_drawdown_pct}%`} color="text-sell" />
              <Stat label="Trades" value={res.trade_count} color="" />
              <Stat label="Avg Win" value={`$${res.avg_win}`} color="text-buy" />
              <Stat label="Avg Loss" value={`$${res.avg_loss}`} color="text-sell" />
              <Stat label="Initial" value={`$${res.initial_equity}`} color="text-[var(--txt-secondary)]" />
            </div>

            <div className="h-[260px] border border-[var(--border-default)] bg-black p-2">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <XAxis dataKey="t" stroke="#555" fontSize={10} />
                  <YAxis stroke="#555" fontSize={10} domain={['dataMin', 'dataMax']} />
                  <Tooltip
                    contentStyle={{ background: '#0A0A0A', border: '1px solid #333', fontFamily: 'JetBrains Mono', fontSize: 11 }}
                    labelStyle={{ color: '#08F7FE' }}
                  />
                  <Line type="monotone" dataKey="equity" stroke="#00FF41" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className="mt-4 max-h-[220px] overflow-y-auto border border-[var(--border-default)]">
              <table className="w-full text-[11px] font-mono">
                <thead className="sticky top-0 bg-[var(--bg-panel)]">
                  <tr className="text-[9px] uppercase tracking-[0.25em] text-cyan-sys border-b border-[var(--border-default)]">
                    <th className="text-left py-1 px-2">entry</th>
                    <th className="text-right py-1 px-2">entry $</th>
                    <th className="text-right py-1 px-2">exit $</th>
                    <th className="text-right py-1 px-2">qty</th>
                    <th className="text-right py-1 px-2">pnl</th>
                    <th className="text-right py-1 px-2">bars</th>
                  </tr>
                </thead>
                <tbody>
                  {res.trades.map((t, i) => (
                    <tr key={i} className="border-b border-[var(--border-default)]">
                      <td className="py-1 px-2 text-[var(--txt-secondary)]">{new Date(t.opened_t).toLocaleDateString()}</td>
                      <td className="py-1 px-2 text-right">{t.entry}</td>
                      <td className="py-1 px-2 text-right">{t.exit}</td>
                      <td className="py-1 px-2 text-right">{t.qty}</td>
                      <td className={`py-1 px-2 text-right ${t.pnl >= 0 ? 'text-buy' : 'text-sell'}`}>{t.pnl}</td>
                      <td className="py-1 px-2 text-right">{t.bars_held}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </CornerPanel>
    </div>
  );
}
